import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { LayoutGrid, TrendingUp } from 'lucide-react';

const COLORS = ['#01796d', '#059669', '#0891b2', '#0284c7', '#7c3aed', '#db2777', '#dc2626', '#ea580c'];

export function CategoriesChart({ data }) {
  // Process the category data
  const processCategories = (inputData) => {
    if (!inputData) return [];

    // Case 1: If data is already in the expected format
    if (Array.isArray(inputData) && inputData.length > 0) {
      // Check if it's already formatted correctly
      if (inputData[0].name !== undefined && inputData[0].sales !== undefined) {
        return inputData;
      }

      // Case 2: If data is from Table9 (API response)
      if (inputData[0].CategoryName !== undefined) {
        return inputData.map(item => ({
          name: item.CategoryName || 'Uncategorized',
          sales: item.SalesAmount || 0,
          quantity: item.Quantity || 0,
          percentage: item.Percentage || 0
        }));
      }

      // Case 3: Generic mapping
      return inputData.map(item => ({
        name: item.name || item.CategoryName || item.category || 'Unknown',
        sales: item.sales || item.SalesAmount || item.amount || 0,
        quantity: item.quantity || item.Quantity || item.qty || 0,
        percentage: item.percentage || item.Percentage || 0
      }));
    }

    // Case 4: If data is an object with categories property
    if (inputData.categories && Array.isArray(inputData.categories)) {
      return inputData.categories.map(item => ({
        name: item.name || item.CategoryName || 'Unknown',
        sales: item.sales || item.SalesAmount || 0,
        quantity: item.quantity || item.Quantity || 0,
        percentage: item.percentage || item.Percentage || 0
      }));
    }

    return [];
  };

  const processedData = processCategories(data);

  // Sort by sales amount (highest first)
  const sortedData = [...processedData].sort((a, b) => b.sales - a.sales);

  // Calculate total sales for verification
  const totalSales = sortedData.reduce((sum, item) => sum + (item.sales || 0), 0);
  const totalQuantity = sortedData.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Ensure each item has a percentage
  const categoriesWithPercentage = sortedData.map(item => ({
    ...item,
    percentage: item.percentage || (totalSales > 0 ? (item.sales / totalSales) * 100 : 0)
  }));

  // Group small categories to avoid label overlap
  const processChartData = (categories) => {
    if (categories.length <= 8) {
      return categories.map(cat => ({
        name: cat.name,
        value: cat.sales,
        quantity: cat.quantity,
        percentage: cat.percentage
      }));
    }

    // Show top 7 categories and group the rest as "Others"
    const topCategories = categories.slice(0, 7);
    const otherCategories = categories.slice(7);
    
    const otherSales = otherCategories.reduce((sum, cat) => sum + cat.sales, 0);
    const otherQuantity = otherCategories.reduce((sum, cat) => sum + cat.quantity, 0);
    const otherPercentage = otherCategories.reduce((sum, cat) => sum + cat.percentage, 0);

    const result = topCategories.map(cat => ({
      name: cat.name,
      value: cat.sales,
      quantity: cat.quantity,
      percentage: cat.percentage
    }));

    if (otherCategories.length > 0) {
      result.push({
        name: 'Others',
        value: otherSales,
        quantity: otherQuantity,
        percentage: otherPercentage
      });
    }

    return result;
  };

  const chartData = processChartData(categoriesWithPercentage);

  // If no data after processing, show empty state
  if (categoriesWithPercentage.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
            <LayoutGrid className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Category Breakdown</h3>
            <p className="text-sm text-slate-500">Sales by category</p>
          </div>
        </div>
        <div className="text-center py-12">
          <LayoutGrid className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-400">No category data available</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-200">
          <p className="font-semibold text-slate-800 mb-2">{data.name}</p>
          <p className="text-sm text-slate-600">
            Sales: Rs. {(data.value || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
          <p className="text-sm text-slate-600">Quantity: {(data.quantity || 0).toLocaleString()}</p>
          <p className="text-sm text-[#01796d] font-medium">{(data.percentage || 0).toFixed(1)}%</p>
        </div>
      );
    }
    return null;
  };

  // Custom label that only shows for larger slices
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percentage, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Only show labels for slices with more than 5%
    if (percentage < 5) return null;

    return (
      <text
        x={x}
        y={y}
        fill="#374151"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={11}
        fontWeight="medium"
      >
        <tspan x={x} dy="0">{name.length > 15 ? name.substring(0, 15) + '...' : name}</tspan>
        <tspan x={x} dy="14" fill="#6b7280" fontSize={10}>
          {percentage.toFixed(0)}%
        </tspan>
      </text>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200 hover:shadow-xl transition-shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
            <LayoutGrid className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Category Breakdown</h3>
            <p className="text-sm text-slate-500">Sales by category</p>
          </div>
        </div>

        {/* Summary stats */}
        <div className="text-right">
          <p className="text-xs text-slate-500">Total Categories</p>
          <p className="text-lg font-bold text-slate-800">{categoriesWithPercentage.length}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        <div className="p-3 col-span-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Sales</p>
          <p className="text-3xl font-bold text-purple-700">
            Rs. {(totalSales || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </p>
        </div>
        <div className="p-3 col-span-2 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total Items</p>
          <p className="text-3xl font-bold text-teal-700">{(totalQuantity || 0).toLocaleString()}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-3">
        <div className="h-96 lg:col-span-3">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                labelLine={true}
                label={renderCustomLabel}
                outerRadius={130}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Category List - Shows all categories including grouped ones */}
        <div className="space-y-2 lg:col-span-2 max-h-[350px] overflow-y-auto pr-2 custom-scrollbar">
          {categoriesWithPercentage.length > 8 && (
            <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700">
                <TrendingUp className="w-3 h-3 inline mr-1" />
                Showing top categories. {categoriesWithPercentage.length - 7} small categories grouped as "Others"
              </p>
            </div>
          )}
          {categoriesWithPercentage.map((category, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg transition-colors border ${
                index < 7 ? 'bg-slate-50 hover:bg-slate-100 border-slate-200' : 
                'bg-gray-50 border-gray-200 opacity-75'
              }`}>
              <div className="flex items-center gap-3">
                <div
                  className="w-4 h-4 rounded-full shadow-md"
                  style={{ backgroundColor: index < 7 ? COLORS[index] : '#9ca3af' }}
                />
                <span className="font-medium text-slate-700 text-sm">
                  {category.name.length > 20 ? category.name.substring(0, 20) + '...' : category.name}
                </span>
              </div>
              <div className="text-right">
                <p className="font-bold text-slate-800 text-sm">
                  Rs. {(category.sales || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </p>
                <div className="flex items-center gap-2 justify-end">
                  <p className="text-xs text-slate-500">Qty: {(category.quantity || 0).toLocaleString()}</p>
                  <span className="text-xs text-purple-600 font-medium">
                    {(category.percentage || 0).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend for chart - only show for chart data (max 8 items) */}
      <div className="mt-4 flex flex-wrap justify-center gap-3">
        {chartData.map((entry, index) => (
          <div key={`legend-${index}`} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-slate-600">{entry.name}</span>
          </div>
        ))}
      </div>

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}